
import {Component, Prop, Vue} from "vue-property-decorator";
import KullaniciIzinleriMatris from "@/components/forms/KullaniciIzinleriMatris.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import {rolRengi, tumModulAnahtarlari} from "@/enum/YetkiModulleri";
import {duzenlemeEngeli} from "@/components/izinler/duzenlemeEngeli";
// HTTP hataları global axios kesicisinde (plugins/axios.ts) gösterilir.

const YUKLEME_HATASI_IPUCU = "Personel türleri yüklenemedi.";

@Component({
  components: {KullaniciIzinleriMatris, DeleteDialog},
})
export default class PersonelTurleriMatrisList extends Vue {
  // DEV-2113 kısıtlı dönem: rol yönetimi kapalı; matris lisanstan muaf (API) olduğu için açık kalır.
  // DEV-2684: ekran yalnız yeni modda çizilir; düğmeler /ben anahtarına bakar (rol-ekle / rol-duzenle /
  // rol-sil = Kullanıcı İzinleri Düzenle + ekle / değiştir / sil). Yalnız Görüntüle'si olan salt-okunur.
  @Prop({type: Boolean, default: false}) islemKisitli!: boolean;

  personelTipleri: Array<any> = [];
  // Seçim rol kimliğiyle tutulur: liste yenilenince (sil, ad değiştir, kaydet) sıra kaysa da aynı rol seçili kalır.
  seciliId: number | null = null;
  // mounted hemen yükler; ilk çizimde de boş liste metni görünmesin.
  yukleniyor = true;
  yuklemeHatasi = false;
  yuklemeNo = 0;

  rolDialog = false;
  dialogModu: "ekle" | "adDegistir" = "ekle";
  dialogAd = "";
  dialogRolId: number | null = null;
  dialogFly = false;

  get seciliIndex(): number | null {
    const i = this.personelTipleri.findIndex((r) => r.id === this.seciliId);
    return i >= 0 ? i : null;
  }

  get seciliRol(): any {
    return this.seciliIndex === null ? null : this.personelTipleri[this.seciliIndex];
  }

  get rolEkleIpucu(): string {
    if (this.islemKisitli) {
      return "Lisans kısıtlı dönemde eklenemez.";
    }
    if (this.yuklemeHatasi) {
      return YUKLEME_HATASI_IPUCU;
    }
    return !this.$helper.isAuthorized("rol-ekle") ? "Bu işlem için yetkiniz yok." : "Personel türü ekle";
  }

  adDegistirIpucu(rol: any): string {
    if (this.islemKisitli) {
      return "Lisans kısıtlı dönemde değiştirilemez.";
    }
    return this.adDegistirmeKilidi(rol) || "Adı değiştir";
  }

  // Ad değiştirme kapalıysa nedeni: yetki (rol-duzenle anahtarı) ya da sunucunun rol satırındaki kilidi
  // (kendi rolü, yönetici rolü). Açıksa null.
  adDegistirmeKilidi(rol: any): string | null {
    if (this.yuklemeHatasi) {
      return YUKLEME_HATASI_IPUCU;
    }
    if (!this.$helper.isAuthorized("rol-duzenle")) {
      return "Bu işlem için yetkiniz yok.";
    }
    return duzenlemeEngeli(rol);
  }

  // Rol kartı noktası: rol indeksine göre palet rengi (kanonik palet, YetkiModulleri).
  rolRengi(index: number | null): string {
    return rolRengi(index === null ? 0 : index);
  }

  // "N modül erişimi": backend `modul_erisim_sayisi` (gizli OLMAYAN modül satır sayısı).
  // Yönetici rolü tüm bölümlere erişir ama satır tutmaz; kullanıcıyı yanıltmamak için
  // toplam modül sayısı gösterilir.
  modulSayisi(rol: any): number {
    if (rol && rol.is_admin) {
      return tumModulAnahtarlari().length;
    }
    return rol && rol.modul_erisim_sayisi ? Number(rol.modul_erisim_sayisi) : 0;
  }

  mounted() {
    this.load();
  }

  // Hata yakalanır: liste boş/bayat kalır ve rol yönetimi kapanır. Yalnız son çağrının sonucu yazılır.
  // Seçilecek: istenen rol, yoksa mevcut seçim; listede değilse ilk rol. Yükleme sürerken kullanıcı
  // başka rol seçtiyse onun seçimi esastır.
  async load(secilecekId: number | null = null) {
    const no = ++this.yuklemeNo;
    const istekteSecili = this.seciliId;
    this.yukleniyor = true;
    let response: any;
    try {
      response = await this.$http.get("/api/v1/roles");
    } catch (hata) {
      if (no === this.yuklemeNo) {
        this.yuklemeHatasi = true;
      }
      return;
    } finally {
      if (no === this.yuklemeNo) {
        this.yukleniyor = false;
      }
    }
    if (no !== this.yuklemeNo) {
      return;
    }
    this.yuklemeHatasi = false;
    this.personelTipleri = response.data || [];
    const hedef = secilecekId !== null && this.seciliId === istekteSecili ? secilecekId : this.seciliId;
    const ilk = this.personelTipleri.length > 0 ? this.personelTipleri[0].id : null;
    this.seciliId = this.personelTipleri.some((r) => r.id === hedef) ? hedef : ilk;
  }

  rolSec(id: number) {
    this.seciliId = id;
  }

  // Matris kaydedilince rol listesini tazele ki "N modül erişimi" güncellensin (seçim korunur).
  izinlerKaydedildi() {
    this.load(this.seciliRol ? this.seciliRol.id : null);
  }

  rolEkleAc() {
    this.dialogModu = "ekle";
    this.dialogAd = "";
    this.dialogRolId = null;
    this.rolDialog = true;
  }

  rolAdiDegistirAc(rol: any) {
    this.dialogModu = "adDegistir";
    this.dialogAd = rol.name;
    this.dialogRolId = rol.id;
    this.rolDialog = true;
  }

  async rolKaydet() {
    // Kaydet düğmesi kapansa da Enter (keyup.enter) buraya gelir; gönderim sürerken ikinci istek gitmez.
    if (!this.dialogAd || this.dialogFly) {
      return;
    }
    this.dialogFly = true;
    try {
      if (this.dialogModu === "ekle") {
        const govde: any = await this.$http.post("/api/v1/roles", {name: this.dialogAd});
        const yeni = govde && govde.data ? govde.data : null;
        this.rolDialog = false;
        this.load(yeni && yeni.id ? yeni.id : null);
        this.$toast.success("Personel türü eklendi.");
      } else if (this.dialogRolId !== null) {
        await this.$http.patch(`/api/v1/roles/${this.dialogRolId}`, {name: this.dialogAd});
        this.rolDialog = false;
        this.load();
        this.$toast.success("Personel türü adı güncellendi.");
      }
    } catch (error) {
      // 403 (yönetici rolü) / 422 (ad kuralı) global kesicide gösterilir.
    } finally {
      this.dialogFly = false;
    }
  }

  // Seçili olmayan rol silinince seçim ve matris (kaydedilmemiş değişikliğiyle) yerinde kalır. Seçili rol
  // silinince matris o rolde kalmaz; yerine sonraki rol, sonuncuysa önceki seçilir.
  rolSilindi(rol: any) {
    if (rol && rol.id === this.seciliId) {
      const i = this.personelTipleri.findIndex((r) => r.id === rol.id);
      const komsu = this.personelTipleri[i + 1] || this.personelTipleri[i - 1] || null;
      this.seciliId = null;
      this.load(komsu ? komsu.id : null);
    } else {
      this.load();
    }
    this.$toast.success("Personel türü silindi.");
  }
}
